import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  ArrowLeft, CheckCircle2, Loader2, Plus, X, Trash2,
  FileText, StickyNote, AlertTriangle, CheckSquare, Ruler, Camera,
  Sparkles, Copy, Download, MapPin, User, Users, Calendar, Clock,
  Zap, Package, ChevronDown, ChevronUp, Pencil, Mail, Send
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Block type definitions ───────────────────────────────────────────────────
const BLOC_TYPES = [
  { type: "note",      label: "Note",         icon: FileText,      bg: "bg-white",       border: "border-l-slate-400",  badge: "bg-slate-100 text-slate-600" },
  { type: "remarque",  label: "Remarque",     icon: StickyNote,    bg: "bg-amber-50",    border: "border-l-amber-400",  badge: "bg-amber-100 text-amber-700" },
  { type: "urgence",   label: "Urgence",      icon: AlertTriangle, bg: "bg-red-50",      border: "border-l-red-500",    badge: "bg-red-100 text-red-700" },
  { type: "decision",  label: "Décision",     icon: CheckSquare,   bg: "bg-blue-50",     border: "border-l-blue-500",   badge: "bg-blue-100 text-blue-700" },
  { type: "mesure",    label: "Mesure",       icon: Ruler,         bg: "bg-indigo-50",   border: "border-l-indigo-400", badge: "bg-indigo-100 text-indigo-700" },
  { type: "photo",     label: "Photo",        icon: Camera,        bg: "bg-slate-50",    border: "border-l-slate-500",  badge: "bg-slate-100 text-slate-700" },
];
const getBlocDef = (type) => BLOC_TYPES.find(b => b.type === type) || BLOC_TYPES[0];

// ─── Save status indicator ─────────────────────────────────────────────────────
function SaveIndicator({ status }) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      {status === 'saving' && <><Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" /><span className="text-white/60 text-xs">Enregistrement...</span></>}
      {status === 'saved'  && <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /><span className="text-white/60 text-xs">Enregistré</span></>}
      {status === 'error'  && <span className="text-red-400 text-xs">Erreur</span>}
    </div>
  );
}

// ─── Text bloc (note / remarque / urgence / decision) ──────────────────────────
function BlocTexte({ bloc, def, onChange, onDelete }) {
  return (
    <div className={`${def.bg} border border-[#E2E8F0] border-l-4 ${def.border} rounded-xl shadow-sm overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E2E8F0]/60">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${def.badge}`}>
          <def.icon className="w-3 h-3" />{def.label}
        </span>
        <button onClick={onDelete} className="p-1.5 hover:bg-red-100 rounded-lg text-[#94A3B8] hover:text-red-500 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <textarea
        value={bloc.texte || ""}
        onChange={e => onChange({ ...bloc, texte: e.target.value })}
        placeholder={`${def.label}... (tapez ici)`}
        className="w-full px-4 py-3 bg-transparent text-[#1E293B] text-lg leading-relaxed resize-none focus:outline-none placeholder-[#CBD5E1] min-h-[100px]"
        rows={4}
      />
    </div>
  );
}

// ─── Mesure bloc ───────────────────────────────────────────────────────────────
function BlocMesure({ bloc, def, onChange, onDelete }) {
  const update = (key, val) => onChange({ ...bloc, [key]: val });
  return (
    <div className={`${def.bg} border border-[#E2E8F0] border-l-4 ${def.border} rounded-xl shadow-sm overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E2E8F0]/60">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${def.badge}`}>
          <Ruler className="w-3 h-3" />Mesure
        </span>
        <button onClick={onDelete} className="p-1.5 hover:bg-red-100 rounded-lg text-[#94A3B8] hover:text-red-500 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <input
          value={bloc.description || ""}
          onChange={e => update("description", e.target.value)}
          placeholder="Élément mesuré (ex: Fenêtre salon, Mur est...)"
          className="w-full h-12 px-4 bg-white border border-[#E2E8F0] rounded-xl text-[#1E293B] text-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 placeholder-[#CBD5E1]"
        />
        <div className="grid grid-cols-3 gap-3">
          {[["largeur","L (m)"],["hauteur","H (m)"],["profondeur","P (m)"]].map(([key, lbl]) => (
            <div key={key}>
              <label className="block text-xs font-bold text-[#64748B] mb-1 uppercase tracking-wide">{lbl}</label>
              <input type="number" value={bloc[key] || ""} onChange={e => update(key, e.target.value)}
                placeholder="0.00" className="w-full h-12 px-3 text-center bg-white border border-[#E2E8F0] rounded-xl text-[#1E293B] text-lg font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-bold text-[#64748B] mb-1 uppercase tracking-wide">Surface (m²)</label>
            <input type="number" value={bloc.surface || ""} onChange={e => update("surface", e.target.value)}
              placeholder="0.00" className="w-full h-12 px-3 text-center bg-white border border-[#E2E8F0] rounded-xl text-[#1E293B] text-lg font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#64748B] mb-1 uppercase tracking-wide">Quantité</label>
            <input type="number" value={bloc.quantite || ""} onChange={e => update("quantite", e.target.value)}
              placeholder="0" className="w-full h-12 px-3 text-center bg-white border border-[#E2E8F0] rounded-xl text-[#1E293B] text-lg font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#64748B] mb-1 uppercase tracking-wide">Unité</label>
            <select value={bloc.unite || "m"} onChange={e => update("unite", e.target.value)}
              className="w-full h-12 px-2 bg-white border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none">
              {["m","cm","mm","m²","m³","pcs","kg","L"].map(u => <option key={u}>{u}</option>)}
            </select>
          </div>
        </div>
        <input value={bloc.remarque || ""} onChange={e => update("remarque", e.target.value)}
          placeholder="Remarque (matériau, couleur, marque...)"
          className="w-full h-11 px-4 bg-white border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none placeholder-[#CBD5E1]" />
      </div>
    </div>
  );
}

// ─── Photo bloc ────────────────────────────────────────────────────────────────
function BlocPhoto({ bloc, def, onChange, onDelete, visiteId }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", bloc.legende || "");
      const res = await axios.post(`${API}/visites/${visiteId}/photos`, fd);
      onChange({ ...bloc, url: res.data.url, filename: res.data.filename, photo_id: res.data.id });
      toast.success("Photo ajoutée");
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className={`${def.bg} border border-[#E2E8F0] border-l-4 ${def.border} rounded-xl shadow-sm overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E2E8F0]/60">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${def.badge}`}>
          <Camera className="w-3 h-3" />Photo
        </span>
        <button onClick={onDelete} className="p-1.5 hover:bg-red-100 rounded-lg text-[#94A3B8] hover:text-red-500 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
        {bloc.url ? (
          <div className="relative rounded-xl overflow-hidden">
            <img src={bloc.url} alt={bloc.legende || "photo"} className="w-full max-h-72 object-cover rounded-xl" />
            <button onClick={() => fileRef.current?.click()}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-xl text-white">
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="w-full h-28 border-2 border-dashed border-[#CBD5E1] rounded-xl flex flex-col items-center justify-center gap-2 text-[#94A3B8] hover:border-blue-400 hover:text-blue-500 transition-colors bg-white">
            {uploading ? <Loader2 className="w-8 h-8 animate-spin" /> : <><Camera className="w-8 h-8" /><span className="text-sm font-medium">Prendre ou sélectionner une photo</span></>}
          </button>
        )}
        <input value={bloc.legende || ""}
          onChange={e => onChange({ ...bloc, legende: e.target.value })}
          placeholder="Légende ou remarque sur cette photo..."
          className="w-full h-11 px-4 bg-white border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none placeholder-[#CBD5E1]" />
      </div>
    </div>
  );
}

// ─── Main Canvas page ──────────────────────────────────────────────────────────
export default function VisiteCanvas() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visite, setVisite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [showBlocPicker, setShowBlocPicker] = useState(false);
  const [chantiers, setChantiers] = useState([]);
  const [showIA, setShowIA] = useState(false);
  const [showRapport, setShowRapport] = useState(false);
  const [analyseIA, setAnalyseIA] = useState(null);
  const [rapport, setRapport] = useState("");
  const [loadingIA, setLoadingIA] = useState(false);
  const [loadingRapport, setLoadingRapport] = useState(false);
  const [copied, setCopied] = useState(false);
  const saveTimer = useRef(null);
  const visiteRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/visites/${id}`),
      axios.get(`${API}/chantiers`),
    ]).then(([vRes, cRes]) => {
      const v = vRes.data;
      if (!v.blocs) v.blocs = [];
      setVisite(v);
      visiteRef.current = v;
      setAnalyseIA(v.analyse_ia || null);
      setRapport(v.rapport || "");
      setChantiers(cRes.data);
    }).catch(() => { toast.error("Visite introuvable"); navigate("/visites"); })
    .finally(() => setLoading(false));
  }, [id]);

  // ─ Auto-save ──────────────────────────────────────────────────────────────────
  const triggerSave = useCallback((newVisite) => {
    visiteRef.current = newVisite;
    setSaveStatus("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/visites/${id}`, newVisite);
        setSaveStatus("saved");
      } catch { setSaveStatus("error"); }
    }, 2000);
  }, [id]);

  const updateField = (key, val) => {
    const updated = { ...visiteRef.current, [key]: val };
    setVisite(updated);
    triggerSave(updated);
  };

  const updateBloc = (idx, bloc) => {
    const blocs = [...(visiteRef.current.blocs || [])];
    blocs[idx] = bloc;
    updateField("blocs", blocs);
  };

  const deleteBloc = (idx) => {
    const blocs = (visiteRef.current.blocs || []).filter((_, i) => i !== idx);
    updateField("blocs", blocs);
  };

  const addBloc = (type) => {
    const newBloc = { id: crypto.randomUUID(), type, texte: "", description: "", largeur: "", hauteur: "", profondeur: "", surface: "", quantite: "", unite: "m", remarque: "", url: "", legende: "", created_at: new Date().toISOString() };
    const blocs = [...(visiteRef.current.blocs || []), newBloc];
    updateField("blocs", blocs);
    setShowBlocPicker(false);
    setTimeout(() => { document.getElementById(`bloc-${blocs.length - 1}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 100);
  };

  const runIA = async () => {
    setLoadingIA(true);
    try {
      const res = await axios.post(`${API}/visites/${id}/analyse-ia`);
      setAnalyseIA(res.data);
      toast.success("Analyse IA terminée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
    finally { setLoadingIA(false); }
  };

  const generateRapport = async () => {
    setLoadingRapport(true);
    try {
      const res = await axios.post(`${API}/visites/${id}/rapport`);
      setRapport(res.data.rapport);
      toast.success("Rapport généré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur rapport"); }
    finally { setLoadingRapport(false); }
  };

  const copyRapport = () => {
    navigator.clipboard.writeText(rapport).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Copié !"); });
  };

  const downloadPDF = () => {
    if (!rapport || !visite) return;
    const doc = new jsPDF({ format: "a4" });
    const margin = 20;
    const pageW = doc.internal.pageSize.width;
    doc.setFillColor(11, 25, 41);
    doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text("VGI CONSTRUCT", margin, 11);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text("Construction & Rénovation", margin, 18);
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    doc.text("COMPTE RENDU DE VISITE", pageW - margin, 18, { align: "right" });
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(248, 250, 252); doc.rect(margin, 34, pageW - 2 * margin, 24, "F");
    doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text(`Chantier: ${visite.chantier_nom}`, margin + 3, 41);
    doc.setFont("helvetica", "normal");
    doc.text(`Client: ${visite.client || "—"}  |  Adresse: ${visite.adresse || "—"}`, margin + 3, 48);
    doc.text(`Date: ${visite.date_visite} ${visite.heure_visite ? "à " + visite.heure_visite : ""}  |  Présents: ${visite.personnes_presentes || "—"}`, pageW / 2, 48);
    const lines = doc.splitTextToSize(rapport, pageW - 2 * margin);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    let y = 66;
    lines.forEach(line => {
      if (y > 278) { doc.addPage(); y = 20; }
      doc.text(line, margin, y); y += 4.5;
    });
    const mesures = (visite.blocs || []).filter(b => b.type === "mesure");
    if (mesures.length > 0 && y < 230) {
      y += 5;
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text("MESURES", margin, y); y += 4;
      doc.autoTable({
        startY: y,
        head: [["Élément", "L", "H", "P", "Surface", "Qté", "Remarque"]],
        body: mesures.map(m => [m.description||"", m.largeur?`${m.largeur}${m.unite}`:"", m.hauteur?`${m.hauteur}${m.unite}`:"", m.profondeur?`${m.profondeur}${m.unite}`:"", m.surface?`${m.surface}m²`:"", m.quantite||"", m.remarque||""]),
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [11, 25, 41], textColor: 255 },
        margin: { left: margin, right: margin },
      });
    }
    const np = doc.internal.getNumberOfPages();
    for (let i = 1; i <= np; i++) { doc.setPage(i); doc.setFontSize(6); doc.setTextColor(150); doc.text(`VGI Construct — ${new Date().toLocaleDateString("fr-FR")} — Page ${i}/${np}`, margin, 290); }
    doc.save(`rapport-${visite.chantier_nom.replace(/\s+/g, "-")}-${visite.date_visite}.pdf`);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
    </div>
  );

  if (!visite) return null;

  const blocs = visite.blocs || [];

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      {/* ── Sticky Header ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-[#0F2744] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/visites")}
            className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Retour</span>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-white text-base uppercase tracking-tight font-['Barlow_Condensed'] truncate">
              {visite.chantier_nom || "Nouvelle visite"}
            </h1>
            {visite.date_visite && <p className="text-white/50 text-xs">{visite.date_visite}{visite.heure_visite ? ` à ${visite.heure_visite}` : ""}</p>}
          </div>
          <SaveIndicator status={saveStatus} />
          <div className="flex gap-1.5 flex-shrink-0">
            <button onClick={() => { setShowIA(!showIA); setShowRapport(false); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${showIA ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
              <Sparkles className="w-3.5 h-3.5" /><span className="hidden sm:inline">IA</span>
            </button>
            <button onClick={() => { setShowRapport(!showRapport); setShowIA(false); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${showRapport ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
              <FileText className="w-3.5 h-3.5" /><span className="hidden sm:inline">Rapport</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4 pb-36">

        {/* ── Info Card ──────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 bg-[#F8FAFC] border-b border-[#E2E8F0] flex items-center gap-2">
            <ClipboardListIcon className="w-4 h-4 text-[#64748B]" />
            <span className="font-bold text-sm text-[#475569] uppercase tracking-wide">Informations de visite</span>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Chantier */}
            <div className="sm:col-span-2 space-y-1.5">
              <label className="text-xs font-bold text-[#64748B] uppercase tracking-wide">Chantier *</label>
              <input list="chantiers-canvas-list"
                value={visite.chantier_nom}
                onChange={e => {
                  const val = e.target.value;
                  const found = chantiers.find(c => c.nom.trim() === val.trim());
                  const updated = { ...visiteRef.current, chantier_nom: val, chantier_id: found ? found.id : "" };
                  setVisite(updated); triggerSave(updated);
                }}
                placeholder="Nom du chantier..."
                className="w-full h-12 px-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] text-lg focus:outline-none focus:ring-2 focus:ring-[#3B82F6] placeholder-[#CBD5E1]"
              />
              <datalist id="chantiers-canvas-list">{chantiers.map(c => <option key={c.id} value={c.nom} />)}</datalist>
            </div>
            {/* Date + Heure */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#64748B] uppercase tracking-wide flex items-center gap-1"><Calendar className="w-3 h-3" />Date</label>
              <input type="date" value={visite.date_visite} onChange={e => updateField("date_visite", e.target.value)}
                className="w-full h-12 px-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#64748B] uppercase tracking-wide flex items-center gap-1"><Clock className="w-3 h-3" />Heure</label>
              <input type="time" value={visite.heure_visite} onChange={e => updateField("heure_visite", e.target.value)}
                className="w-full h-12 px-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" />
            </div>
            {/* Client + Email */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#64748B] uppercase tracking-wide flex items-center gap-1"><User className="w-3 h-3" />Client</label>
              <input value={visite.client} onChange={e => updateField("client", e.target.value)}
                placeholder="Nom du client..."
                className="w-full h-12 px-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-[#3B82F6] placeholder-[#CBD5E1]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#64748B] uppercase tracking-wide flex items-center gap-1"><Mail className="w-3 h-3" />Email client</label>
              <input type="email" value={visite.client_email || ""} onChange={e => updateField("client_email", e.target.value)}
                placeholder="client@exemple.com"
                className="w-full h-12 px-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-[#3B82F6] placeholder-[#CBD5E1]" />
            </div>
            {/* Adresse */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#64748B] uppercase tracking-wide flex items-center gap-1"><MapPin className="w-3 h-3" />Adresse</label>
              <input value={visite.adresse} onChange={e => updateField("adresse", e.target.value)}
                placeholder="Rue, Ville..."
                className="w-full h-12 px-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-[#3B82F6] placeholder-[#CBD5E1]" />
            </div>
            {/* Personnes */}
            <div className="sm:col-span-2 space-y-1.5">
              <label className="text-xs font-bold text-[#64748B] uppercase tracking-wide flex items-center gap-1"><Users className="w-3 h-3" />Personnes présentes</label>
              <input value={visite.personnes_presentes} onChange={e => updateField("personnes_presentes", e.target.value)}
                placeholder="Ex: Daniel (VGI), M. Lambert (client), Architecte..."
                className="w-full h-12 px-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] text-base focus:outline-none focus:ring-2 focus:ring-[#3B82F6] placeholder-[#CBD5E1]" />
            </div>
          </div>
        </div>

        {/* ── Blocs ──────────────────────────────────────────────────────────── */}
        {blocs.length === 0 && (
          <div className="text-center py-10 text-[#94A3B8]">
            <div className="w-16 h-16 bg-white rounded-2xl border-2 border-dashed border-[#CBD5E1] flex items-center justify-center mx-auto mb-3">
              <Plus className="w-8 h-8 text-[#CBD5E1]" />
            </div>
            <p className="font-semibold text-[#64748B] text-lg">Commencez à documenter</p>
            <p className="text-sm mt-1">Ajoutez des notes, mesures, photos...</p>
          </div>
        )}

        {blocs.map((bloc, idx) => {
          const def = getBlocDef(bloc.type);
          const key = `bloc-${idx}`;
          return (
            <div key={bloc.id || idx} id={key}>
              {bloc.type === "mesure" ? (
                <BlocMesure bloc={bloc} def={def} onChange={b => updateBloc(idx, b)} onDelete={() => deleteBloc(idx)} />
              ) : bloc.type === "photo" ? (
                <BlocPhoto bloc={bloc} def={def} onChange={b => updateBloc(idx, b)} onDelete={() => deleteBloc(idx)} visiteId={id} />
              ) : (
                <BlocTexte bloc={bloc} def={def} onChange={b => updateBloc(idx, b)} onDelete={() => deleteBloc(idx)} />
              )}
            </div>
          );
        })}

        {/* ── IA Section ─────────────────────────────────────────────────────── */}
        {showIA && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden">
            <div className="bg-[#F5F3FF] px-5 py-3.5 border-b border-purple-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <span className="font-bold text-purple-800 uppercase tracking-wide text-sm">Analyse IA</span>
              </div>
              <button onClick={runIA} disabled={loadingIA}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
                {loadingIA ? <><Loader2 className="w-4 h-4 animate-spin" />Analyse...</> : <><Sparkles className="w-4 h-4" />{analyseIA ? "Ré-analyser" : "Analyser"}</>}
              </button>
            </div>
            {analyseIA ? (
              <div className="p-5 space-y-4">
                {analyseIA.urgences?.length > 0 && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                    <p className="font-bold text-red-700 mb-2 flex items-center gap-2 text-sm"><Zap className="w-4 h-4" />Urgences</p>
                    <ul className="space-y-1.5">{analyseIA.urgences.map((u,i) => <li key={i} className="flex items-start gap-2 text-sm text-red-600"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0" />{u}</li>)}</ul>
                  </div>
                )}
                {analyseIA.problemes?.length > 0 && (
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                    <p className="font-bold text-orange-700 mb-2 flex items-center gap-2 text-sm"><AlertTriangle className="w-4 h-4" />Problèmes</p>
                    <ul className="space-y-1.5">{analyseIA.problemes.map((p,i) => <li key={i} className="flex items-start gap-2 text-sm text-orange-600"><span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0" />{p}</li>)}</ul>
                  </div>
                )}
                {analyseIA.actions?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <p className="font-bold text-blue-700 mb-2 text-sm">Actions recommandées</p>
                    <ul className="space-y-1.5">{analyseIA.actions.map((a,i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-blue-600">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold mt-0.5 flex-shrink-0 ${a.priorite==="haute"?"bg-red-500 text-white":a.priorite==="basse"?"bg-slate-300 text-slate-600":"bg-blue-400 text-white"}`}>{(a.priorite||"").toUpperCase()}</span>
                        {a.description}
                      </li>
                    ))}</ul>
                  </div>
                )}
                {analyseIA.impacts && (
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                    <p className="font-bold text-[#475569] mb-3 flex items-center gap-2 text-sm"><Package className="w-4 h-4" />Impacts estimés</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[["Coût","cout"],["Délai","delai"],["Matériel","materiel"]].map(([lbl,key]) => (
                        <div key={key} className="text-center bg-white rounded-xl p-3 border border-[#E2E8F0]">
                          <p className="text-xs text-[#94A3B8] mb-1">{lbl}</p>
                          <p className="text-sm font-semibold text-[#1E293B]">{analyseIA.impacts[key] || "—"}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-[#94A3B8]">
                <Sparkles className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Cliquez "Analyser" pour identifier urgences et actions</p>
              </div>
            )}
          </div>
        )}

        {/* ── Rapport Section ─────────────────────────────────────────────────── */}
        {showRapport && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden">
            <div className="bg-[#F0FDF4] px-5 py-3.5 border-b border-emerald-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                <span className="font-bold text-emerald-800 uppercase tracking-wide text-sm">Compte rendu</span>
              </div>
              <button onClick={generateRapport} disabled={loadingRapport}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
                {loadingRapport ? <><Loader2 className="w-4 h-4 animate-spin" />Génération...</> : <><FileText className="w-4 h-4" />{rapport ? "Regénérer" : "Générer"}</>}
              </button>
            </div>
            {rapport ? (
              <div className="p-5 space-y-4">
                <div className="bg-[#F8FAFC] rounded-xl p-4 max-h-80 overflow-y-auto border border-[#E2E8F0]">
                  <pre className="text-sm text-[#1E293B] whitespace-pre-wrap font-sans leading-relaxed">{rapport}</pre>
                </div>

                {/* ── Share panel ───────────────────────────────────────────── */}
                <div className="space-y-3">
                  <p className="text-xs font-bold text-[#64748B] uppercase tracking-wide">Partager le rapport</p>

                  {/* Email button — full width, primary */}
                  <button
                    data-testid="btn-share-email"
                    onClick={() => {
                      const email = visite.client_email || "";
                      const subject = encodeURIComponent(`Compte rendu de visite — ${visite.chantier_nom} — ${visite.date_visite}`);
                      const body = encodeURIComponent(rapport);
                      window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
                    }}
                    className="w-full h-14 rounded-2xl font-bold text-base transition-all flex items-center justify-center gap-3 bg-[#3B82F6] hover:bg-[#2563EB] active:scale-[0.98] text-white shadow-md shadow-blue-200"
                  >
                    <Mail className="w-5 h-5" />
                    <span>
                      {visite.client_email
                        ? `Envoyer à ${visite.client_email}`
                        : "Envoyer par email"}
                    </span>
                    <Send className="w-4 h-4 opacity-70" />
                  </button>

                  {/* Email hint if no address */}
                  {!visite.client_email && (
                    <p className="text-xs text-[#94A3B8] text-center -mt-1">
                      Ajoutez l'email du client dans les infos de visite pour le pré-remplir
                    </p>
                  )}

                  {/* Copy + PDF row */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      data-testid="btn-copy-rapport"
                      onClick={copyRapport}
                      className={`flex items-center justify-center gap-2 h-12 rounded-xl border-2 font-semibold text-sm transition-colors ${copied ? "border-emerald-400 bg-emerald-50 text-emerald-600" : "border-[#E2E8F0] text-[#475569] hover:border-[#3B82F6] hover:text-[#3B82F6]"}`}
                    >
                      {copied ? <><CheckCircle2 className="w-4 h-4" />Copié !</> : <><Copy className="w-4 h-4" />Copier</>}
                    </button>
                    <button
                      data-testid="btn-download-pdf"
                      onClick={downloadPDF}
                      className="flex items-center justify-center gap-2 h-12 rounded-xl bg-[#0F2744] hover:bg-[#1A3557] text-white font-semibold text-sm transition-colors"
                    >
                      <Download className="w-4 h-4" />PDF
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-[#94A3B8]">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Cliquez "Générer" pour créer le compte rendu</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Bottom Floating Bar ──────────────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-30 safe-area-bottom">
        <div className="max-w-4xl mx-auto px-4 pb-4 pt-2">
          <button
            data-testid="btn-add-bloc"
            onClick={() => setShowBlocPicker(true)}
            className="w-full h-16 bg-[#F97316] hover:bg-orange-500 active:scale-[0.98] text-white rounded-2xl font-bold text-lg shadow-xl shadow-orange-200 transition-all flex items-center justify-center gap-3 uppercase tracking-wide font-['Barlow_Condensed']"
          >
            <Plus className="w-6 h-6" />
            Ajouter un élément
          </button>
        </div>
      </div>

      {/* ── Bloc Picker Sheet ─────────────────────────────────────────────────── */}
      {showBlocPicker && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setShowBlocPicker(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full bg-white rounded-t-3xl shadow-2xl p-5 pb-8 max-w-4xl mx-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-xl text-[#0F2744] uppercase tracking-tight font-['Barlow_Condensed']">Ajouter un élément</h3>
              <button onClick={() => setShowBlocPicker(false)} className="p-2 hover:bg-[#F1F5F9] rounded-xl"><X className="w-5 h-5 text-[#64748B]" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {BLOC_TYPES.map(bt => (
                <button
                  key={bt.type}
                  onClick={() => addBloc(bt.type)}
                  className={`${bt.bg} border border-[#E2E8F0] border-l-4 ${bt.border} rounded-2xl p-4 flex flex-col items-center gap-2 hover:shadow-md active:scale-95 transition-all`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bt.badge}`}>
                    <bt.icon className="w-5 h-5" />
                  </div>
                  <span className="font-bold text-sm text-[#1E293B]">{bt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper icon (ClipboardList wasn't imported above)
function ClipboardListIcon(props) {
  return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>;
}
